import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable} from "mobx";
import {ModalType, RequestState, TeamRegion} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {IUserStore} from "data/stores/user/user.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IPlayer, IPlayersStore} from "data/stores/players/players.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {SyntheticEvent} from "react";
import type {ITeamStore} from "data/stores/team/team.store";
import type {IPlayerPoolStore} from "data/stores/player_pool/player_pool.store";
import {includes, size} from "lodash";
import type {IShareStore} from "data/stores/share/share.store";

export interface IGameController extends ViewController {
	isLoading: boolean;

	get i18n(): ILocalizationStore;
	get playersList(): IPlayer[];
	get tabRegionValue(): TeamRegion;
	get isSelectedStarted(): boolean;
	get selectedRegion(): TeamRegion;
	get currentLineup(): number[];
	get allLineup(): number[];
	get textObject(): Record<string, string>;
	get isSelectedTeamComplete(): boolean;
	get euLineup(): number[];
	get usLineup(): number[];
	get isEuTeamSaved(): boolean;
	get isUsTeamSaved(): boolean;
	get isTeamSavedAfterLogin(): boolean;
	get isFinalSaved(): boolean;

	onOpenPoolModal: () => void;
	handleChangeRegionTab: (_event: SyntheticEvent, newValue: TeamRegion) => void;
}

@injectable()
export class GameController implements IGameController {
	@observable isLoading: boolean = false;
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable _tabsRegionValue = TeamRegion.Europe;

	get i18n() {
		return this._i18nStore;
	}

	get playersList() {
		return this._playerStore.list;
	}

	get tabRegionValue() {
		return this._tabsRegionValue;
	}

	get isSelectedStarted() {
		return this._teamStore.isSelectedStart;
	}

	get selectedRegion() {
		return this._teamStore.selectedRegion;
	}

	get currentLineup() {
		return this._teamStore.currentLineup;
	}

	get allLineup() {
		return this._teamStore.allLineup;
	}

	get isSelectedTeamComplete() {
		return this._teamStore.isSelectTeamCompleted;
	}

	get euLineup() {
		return this._teamStore.euLineup;
	}

	get usLineup() {
		return this._teamStore.usLineup;
	}

	get isEuTeamSaved() {
		return this._teamStore.isEuTeamSaved;
	}

	get isUsTeamSaved() {
		return this._teamStore.isUsTeamSaved;
	}

	get isFinalSaved() {
		return this._teamStore.isFinalSaved;
	}

	get isTeamSavedAfterLogin() {
		return this._teamStore.isTeamSavedAfterLogin;
	}

	get textObject() {
		const i18n = this._i18nStore;
		const currentStep = this._teamStore.currentStep;
		const selectedRegion = this._teamStore.selectedRegion;
		const isEurope = selectedRegion === TeamRegion.Europe;
		const teamFirstStep = includes([0, 2], currentStep);

		const euAutoTitleCopy = i18n.t("page.game.eu_auto_title", "Team europe - auto qualifiers");
		const euCaptainTitleCopy = i18n.t(
			"page.game.eu_captain_title",
			"Team europe - captain’s picks"
		);
		const euAutoDescriptionCopy = i18n.t(
			"page.game.eu_auto_description",
			"Are you happy with these top 6 qualifiers? Edit your line up by removing golfers to build your top 6"
		);
		const euCaptainDescriptionCopy = i18n.t(
			"page.game.eu_captain_description",
			"Add your unique 6 golfer picks to build out the rest of your Team Europe"
		);

		const usAutoTitleCopy = i18n.t("page.game.us_auto_title", "Team USA - auto qualifiers");
		const usCaptainTitleCopy = i18n.t(
			"page.game.us_captain_title",
			"Team USA - captain’s picks"
		);
		const usAutoDescriptionCopy = i18n.t(
			"page.game.us_auto_description",
			"Are you happy with these top 6 qualifiers? Edit your line up by removing golfers to build your top 6"
		);
		const usCaptainDescriptionCopy = i18n.t(
			"page.game.us_captain_description",
			"Add your unique 6 golfer picks to build out the rest of your Team USA"
		);

		if (isEurope) {
			return {
				title: teamFirstStep ? euAutoTitleCopy : euCaptainTitleCopy,
				description: teamFirstStep ? euAutoDescriptionCopy : euCaptainDescriptionCopy,
			};
		} else {
			return {
				title: teamFirstStep ? usAutoTitleCopy : usCaptainTitleCopy,
				description: teamFirstStep ? usAutoDescriptionCopy : usCaptainDescriptionCopy,
			};
		}
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.PlayersStore) private _playerStore: IPlayersStore,
		@inject(Bindings.PlayerPoolStore) private _poolStore: IPlayerPoolStore,
		@inject(Bindings.ModalsStore) private _modalStore: IModalsStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore,
		@inject(Bindings.ShareStore) private _shareStore: IShareStore
	) {
		makeAutoObservable(this);
	}

	handleChangeRegionTab = (_event: SyntheticEvent, newValue: TeamRegion) => {
		this._tabsRegionValue = newValue;
	};

	setIsLoading(value: boolean): void {
		this.isLoading = value;
	}

	onOpenPoolModal = () => {
		this._poolStore.togglePoolModal();
	};

	onSkip = () => {
		this._modalStore.showModal(ModalType.SKIP, {
			message: "",
		});
	};

	dispose() {
		return;
	}

	async init(param: void) {
		this.setIsLoading(true);

		// this._teamStore.setIsSelectedStart();
		// this._teamStore.setIsPromptActive();

		await this._playerStore.fetchPlayers();
		this._tabsRegionValue = this._teamStore.firstSelectedRegion;

		if (!this._teamStore.isSelectTeamCompleted && !this._teamStore.isTeamSavedAfterLogin) {
			this._teamStore.autoFillLineup(TeamRegion.Europe);
			this._teamStore.autoFillLineup(TeamRegion.Usa);
		}
		this._teamStore.setTopRankPlayers();

		const euLineup = JSON.parse(localStorage.getItem("euLineup") || "[]") as number[];
		const usLineup = JSON.parse(localStorage.getItem("usLineup") || "[]") as number[];

		if ([size(euLineup), size(usLineup)].every(Boolean)) {
			this._teamStore.setLineup(TeamRegion.Europe, euLineup);
			this._teamStore.setLineup(TeamRegion.Usa, usLineup);
		}

		if (this._teamStore.isTeamSavedAfterLogin) {
			const type = this._shareStore.getShareType;
			void this._shareStore.shareTeam({lang: this._i18nStore.lang, type: type});
			this._shareStore.openShareModal();
		}

		this.setIsLoading(false);
	}
}
